import { motion } from "motion/react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import type { StackableItem } from "../../lib/types";
import type { SwipeDirection } from "../../hooks/useCardRotation";
import { DraggableContainer } from "./DraggableContainer";

interface StackConfig {
  rotation: number;
  scale: number;
  perspective: number;
}

interface CardStackProps<T extends StackableItem> {
  items: T[];
  children: (item: T) => ReactNode;
  containerClassName?: string;
  cardClassName?: string;
  stackConfig?: Partial<StackConfig>;
  onActiveChange?: (item: T | null) => void;
  onItemSwipe?: (item: T, direction: SwipeDirection) => void;
  maxVisibleCards?: number;
}

const defaultConfig: StackConfig = {
  rotation: 4,
  scale: 0.06,
  perspective: 600,
};

const DEFAULT_MAX_VISIBLE_CARDS = 8;

export function CardStack<T extends StackableItem>({
  items: initialItems,
  children,
  containerClassName = "relative h-52 w-52",
  cardClassName = "absolute h-52 w-52 cursor-grab",
  stackConfig: userConfig = {},
  onActiveChange,
  onItemSwipe,
  maxVisibleCards = DEFAULT_MAX_VISIBLE_CARDS,
}: CardStackProps<T>) {
  const [items, setItems] = useState(initialItems);
  const [dismissedIds, setDismissedIds] = useState<Set<T["id"]>>(
    () => new Set()
  );
  const config = { ...defaultConfig, ...userConfig };
  const safeMaxVisible =
    Number.isFinite(maxVisibleCards) && maxVisibleCards > 0
      ? Math.floor(maxVisibleCards)
      : DEFAULT_MAX_VISIBLE_CARDS;

  const visibleItems = useMemo<T[]>(() => {
    if (items.length <= safeMaxVisible) {
      return items;
    }
    return items.slice(items.length - safeMaxVisible);
  }, [items, safeMaxVisible]);

  useEffect(() => {
    setDismissedIds((previous) => {
      const incomingIds = new Set(initialItems.map((item) => item.id));
      let changed = false;
      const next = new Set<T["id"]>();

      previous.forEach((id) => {
        if (incomingIds.has(id)) {
          next.add(id);
        } else {
          changed = true;
        }
      });

      return changed ? next : previous;
    });
  }, [initialItems]);

  useEffect(() => {
    const incomingMap = new Map(initialItems.map((item) => [item.id, item]));

    setItems((previous) => {
      const next: T[] = [];
      const seen = new Set<T["id"]>();

      for (const previousItem of previous) {
        if (!incomingMap.has(previousItem.id)) {
          continue;
        }
        if (dismissedIds.has(previousItem.id)) {
          continue;
        }
        const updatedItem = incomingMap.get(previousItem.id) as T;
        next.push(updatedItem);
        seen.add(previousItem.id);
      }

      for (const incomingItem of initialItems) {
        if (dismissedIds.has(incomingItem.id)) {
          continue;
        }
        if (seen.has(incomingItem.id)) {
          continue;
        }
        next.push(incomingItem);
      }

      return next;
    });
  }, [initialItems, dismissedIds]);

  useEffect(() => {
    if (onActiveChange) {
      const active = items.length > 0 ? items[items.length - 1] : null;
      onActiveChange(active || null);
    }
  }, [items, onActiveChange]);

  const sendToBack = (id: T["id"]) => {
    setItems((prev) => {
      const newItems = [...prev];
      const index = newItems.findIndex((item) => item.id === id);
      if (index < 0) {
        return prev;
      }
      const [item] = newItems.splice(index, 1);
      if (!item) {
        return prev;
      }
      newItems.unshift(item);
      return newItems;
    });
  };

  const handleSwipe = (item: T, direction: SwipeDirection) => {
    if (direction === "left") {
      setDismissedIds((previous) => {
        if (previous.has(item.id)) {
          return previous;
        }
        const next = new Set(previous);
        next.add(item.id);
        return next;
      });

      setItems((prev) => {
        const index = prev.findIndex((entry) => entry.id === item.id);
        if (index < 0) {
          return prev;
        }
        const newItems = [...prev];
        newItems.splice(index, 1);
        return newItems;
      });
      onItemSwipe?.(item, direction);
      return;
    }

    sendToBack(item.id);
    onItemSwipe?.(item, direction);
  };

  return (
    <div
      className={containerClassName}
      style={{ perspective: config.perspective }}
    >
      {visibleItems.map((item, index) => {
        const depth = visibleItems.length - index - 1;
        return (
          <DraggableContainer
            key={item.id}
            onSwipe={(direction) => handleSwipe(item, direction)}
            className={cardClassName}
          >
            <motion.div
              className="h-full w-full"
              animate={{
                rotateZ: depth * config.rotation,
                scale: 1 - depth * config.scale,
                transformOrigin: "90% 90%",
              }}
              transition={{ type: "spring", stiffness: 260, damping: 20 }}
            >
              {children(item)}
            </motion.div>
          </DraggableContainer>
        );
      })}
    </div>
  );
}
